import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ReportRowComparison } from "../utils/rowUtils";

const StyledDialog = styled(Dialog)`
  width: 90vw;
  max-width: 600px;

  .p-dialog-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const IntroText = styled.p`
  margin: 0 0 0.5rem 0;
`;

const ChangeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e1e1e1;

  & span:first-child {
    font-weight: 500;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

interface RefreshReportRowDialogProps {
  visible: boolean;
  comparison: ReportRowComparison | null;
  onConfirm: () => void;
  onHide: () => void;
  isSaving?: boolean;
}

const RefreshReportRowDialog: React.FC<RefreshReportRowDialogProps> = ({
  visible,
  comparison,
  onConfirm,
  onHide,
  isSaving,
}) => {
  const { t } = useTranslation();

  if (!comparison) {
    return null;
  }

  return (
    <StyledDialog
      header={t("title-refresh-row-changes")}
      visible={visible}
      onHide={onHide}
      modal
    >
      <IntroText>{t("text-confirm-refresh-row-changes")}</IntroText>

      {comparison.nameChanged && (
        <ChangeRow>
          <span>{t("label-product-name")}</span>
          <span>
            {comparison.oldName} {"\u2192"} {comparison.newName}
          </span>
        </ChangeRow>
      )}

      {comparison.activeChanged && (
        <ChangeRow>
          <span>{t("label-responsible-to-report")}</span>
          <span>
            {comparison.oldActive ? t("cmd-yes") : t("cmd-no")} {"\u2192"}{" "}
            {comparison.newActive ? t("cmd-yes") : t("cmd-no")}
          </span>
        </ChangeRow>
      )}

      {comparison.totalWeightChanged && (
        <ChangeRow>
          <span>{t("label-amount-kg")}</span>
          <span>
            {comparison.oldTotalWeight.toFixed(2)} {"\u2192"}{" "}
            {comparison.newTotalWeight.toFixed(2)} kg
          </span>
        </ChangeRow>
      )}

      {comparison.valueChanges.map((change, index) => (
        <ChangeRow key={index}>
          <span>{change.label}</span>
          <span>
            {change.oldWeight.toFixed(2)} {"\u2192"}{" "}
            {change.newWeight.toFixed(2)} kg
          </span>
        </ChangeRow>
      ))}

      <ButtonContainer>
        <Button label={t("cmd-cancel")} outlined onClick={onHide} />
        <Button
          label={t("cmd-confirm")}
          onClick={onConfirm}
          loading={isSaving}
        />
      </ButtonContainer>
    </StyledDialog>
  );
};

export default RefreshReportRowDialog;
